class BaseDrawer extends HTMLElement {
  constructor() {
    super();
    this.setId();

    this.toggles = [...document.querySelectorAll(`[data-trigger-drawer="${this.id}"]`)];
    this.overlay = document.querySelector('[data-global-overlay]');
    this.focusTrap = null;
    this.scrollY = 0;
    this.lastActiveElement = null; // store trigger for focus restore

    // Bind handlers
    this.handleToggleClick = (event) => {
      if (event.currentTarget.hasAttribute('data-close-all')) {
        document.querySelectorAll('.drawer--open').forEach((drawer) => drawer.close());
        return;
      }

      this.lastActiveElement = event.currentTarget; // save the opener
      this.toggle();
    };

    this.handleOverlayClick = (event) => {
      if (event.target === this.overlay && this.classList.contains('drawer--open')) {
        this.close();
      }
    };

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isTopmost()) {
        this.close();
      }
    };

    this.addEventListeners();
  }

  /* ---------------------------------- */
  /* Body scroll lock (iOS-safe)        */
  /* ---------------------------------- */
  lockBodyScroll() {
    if (document.body.classList.contains('body-scroll-locked')) return;

    this.scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    document.body.classList.add('body-scroll-locked');
  }

  unlockBodyScroll() {
    if (!document.body.classList.contains('body-scroll-locked')) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    document.body.classList.remove('body-scroll-locked');

    window.scrollTo(0, this.scrollY);
  }
  /* ---------------------------------- */

  setId() {
    const id = this.getAttribute('data-id');
    if (!id) {
      throw new Error('Drawer must have a data-id attribute');
    }
    this.id = id;
  }

  addEventListeners() {
    this.toggles.forEach(toggle =>
      toggle.addEventListener('click', this.handleToggleClick)
    );

    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    window.addEventListener('keydown', this.handleKeydown);
  }

  removeEventListeners() {
    this.toggles.forEach(toggle =>
      toggle.removeEventListener('click', this.handleToggleClick)
    );

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }

    window.removeEventListener('keydown', this.handleKeydown);
  }

  // Drawers stack in DOM order, so the last open one is the one on top.
  isTopmost() {
    const open = document.querySelectorAll('.drawer--open');
    return open[open.length - 1] === this;
  }

  toggle() {
    this.classList.contains('drawer--open') ? this.close() : this.open();
  }

  open() {
    if (this.classList.contains('drawer--open')) return;

    this.lockBodyScroll();

    this.classList.add('drawer--open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');

    if (this.overlay) {
      this.overlay.classList.add('drawer-overlay--open');
      this.overlay.setAttribute('aria-hidden', 'false');
    }

    if (!this.focusTrap && window.focusTrap) {
      this.focusTrap = window.focusTrap.createFocusTrap(this, {
        escapeDeactivates: false, // handled manually
        returnFocusOnDeactivate: false, // we handle focus manually
        allowOutsideClick: (event) =>
          this.overlay &&
          (event.target === this.overlay || this.overlay.contains(event.target))
      });
    }

    // Activate trap immediately. It throws when nothing inside is tabbable yet; the drawer must still open.
    try {
      this.focusTrap?.activate();
    } catch (error) {
      console.warn('Drawer focus trap not activated', error);
    }
  }

  close() {
    if (!this.classList.contains('drawer--open')) return;

    // 1. Deactivate focus trap
    this.focusTrap?.deactivate();

    // 2. Restore focus to the element that opened the drawer
    if (this.lastActiveElement instanceof HTMLElement) {
      this.lastActiveElement.focus();
    }

    // 3. Now safe to hide from assistive tech
    this.setAttribute('aria-hidden', 'true');

    this.classList.remove('drawer--open');

    // A drawer underneath (e.g. the menu below a sub-menu) keeps the page locked and the overlay up.
    if (document.querySelector('.drawer--open')) return;

    // 4. Unlock body scroll
    this.unlockBodyScroll();

    // 5. Visual cleanup
    document.body.classList.remove('drawer-open');

    if (this.overlay) {
      this.overlay.classList.remove('drawer-overlay--open');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
  }

  connectedCallback() {
    console.log('Drawer connected');
  }

  disconnectedCallback() {
    this.focusTrap?.deactivate();
    this.unlockBodyScroll();
    this.removeEventListeners();
  }
}

export default BaseDrawer;